import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { api } from '../api';
import { Profile, ClientProfile, EffectivePermission } from '../api/profile';
import { Subscription } from '../api/billing';
import { WorkgroupMembership } from '../api/auth';

interface AuthContextType {
  user: Profile | null;
  clientProfile: ClientProfile | null;
  subscription: Subscription | null;
  workgroups: WorkgroupMembership[];
  activeWorkgroupId: number | null;
  workgroupEpoch: number;
  permissions: EffectivePermission[];
  entitlements: EffectivePermission[];
  /** Whether the active workgroup's role (merged with any per-member override) grants `action` on `module`. */
  hasPermission: (module: string, action: string) => boolean;
  /** Whether the active workgroup's current package entitles `action` on `module`. Independent of role -- AND it with hasPermission via useCanAccess rather than gating on this alone. */
  hasEntitlement: (module: string, action: string) => boolean;
  login: (credentials: { emailid: string; password: string }) => Promise<Profile>;
  signup: (userInfo: { name: string; emailid: string; password: string; profileType: string }) => Promise<void>;
  githubLogin: () => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  switchWorkgroup: (workgroupId: number) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

const mapToClientProfile = (
  profile: Profile,
  billingAddress?: { title: string; address?: string; city?: string; statename?: string; pincode?: string; country?: string } | null
): ClientProfile => {
  return {
    id: profile.id,
    firstName: profile.firstname || '',
    lastName: profile.lastname || '',
    emailid: profile.emailid || '',
    mobile: profile.mobile || '',
    designation: profile.designation || '',
    department: profile.department || '',
    role: profile.role || '',
    timezone: profile.timezone || '',
    avatar: profile.avatar || '',
    billingAddress: billingAddress ? {
      title: billingAddress.title || '',
      address: billingAddress.address || '',
      city: billingAddress.city || '',
      statename: billingAddress.statename || '',
      pincode: billingAddress.pincode || '',
      country: billingAddress.country || '',
    } : undefined

  };
};

/** Reads the `workgroupid` claim off the currently stored access token, without a network round-trip. */
const readActiveWorkgroupIdFromToken = (): number | null => {
  const tokens = api.auth.getStoredTokens();
  if (!tokens?.accessToken) return null;
  try {
    const payload = JSON.parse(atob(tokens.accessToken.split('.')[1]));
    return payload?.workgroupid ?? null;
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<Profile | null>(null);
  const [clientProfile, setClientProfile] = useState<ClientProfile | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [workgroups, setWorkgroups] = useState<WorkgroupMembership[]>([]);
  const [activeWorkgroupId, setActiveWorkgroupId] = useState<number | null>(null);
  const [workgroupEpoch, setWorkgroupEpoch] = useState(0);
  const [permissions, setPermissions] = useState<EffectivePermission[]>([]);
  const [entitlements, setEntitlements] = useState<EffectivePermission[]>([]);
  const [loading, setLoading] = useState(true);

  const hasPermission = useCallback(
    (module: string, action: string) =>
      permissions.some((p) => p.modulecode === module && p.actions.includes(action)),
    [permissions],
  );

  const hasEntitlement = useCallback(
    (module: string, action: string) =>
      entitlements.some((p) => p.modulecode === module && p.actions.includes(action)),
    [entitlements],
  );

  /** Loads profile, permissions, entitlements, workgroup memberships, and workgroup-scoped billing data (subscription + address) for whichever workgroup the current token is active in. */
  const loadSessionData = useCallback(async (profileData: Profile, permissionData: EffectivePermission[] = [], entitlementData: EffectivePermission[] = []) => {
    const workgroupId = readActiveWorkgroupIdFromToken();
    setActiveWorkgroupId(workgroupId);

    // These three calls are independent of each other (none needs another's
    // result -- workgroupId is already known), so run them concurrently
    // instead of paying the sum of their latencies on every login/mount/switch.
    const [workgroupsResult, addressResult, subscriptionResult] = await Promise.allSettled([
      api.auth.getWorkgroups(),
      // The billing address is Owner-only (billing.read); skip the call for other roles instead of eliciting a 403 toast on every login.
      workgroupId && permissionData.some((p) => p.modulecode === 'billing' && p.actions.includes('read'))
        ? api.profiles.getOrganisationAddress(workgroupId)
        : Promise.resolve(null),
      api.billing.getCurrentPackage(),
    ]);

    setWorkgroups(workgroupsResult.status === 'fulfilled' ? workgroupsResult.value.data || [] : []);

    const billingAddress = addressResult.status === 'fulfilled' ? addressResult.value?.data || null : null;
    setClientProfile(mapToClientProfile(profileData, billingAddress));

    setSubscription(subscriptionResult.status === 'fulfilled' ? subscriptionResult.value.data || null : null);
    setPermissions(permissionData);
    setEntitlements(entitlementData);

    // Set last (React 18 batches these with the setters above into one render),
    // so nothing ever observes `user` truthy while `subscription` is still null.
    // Setting it first used to open exactly that gap during login/refreshUser -
    // App.tsx's onboarding gate reads that combination as "no plan yet" and would
    // flash AccountSetup for a frame even for an existing, fully set-up account.
    setUser(profileData);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.auth.setToken(token);
      api.profiles.getProfile()
        .then(async (response) => {
          if (response.data) {
            await loadSessionData(response.data.user, response.data.permission, response.data.entitlement);
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          api.auth.setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [loadSessionData]);

  const login = async (credentials: { emailid: string; password: string }) => {
    const response = await api.auth.login({
      emailid: credentials.emailid,
      passwordhash: credentials.password
    });
    if (response.data) {
      localStorage.setItem('token', response.data.accessToken);
      api.auth.setToken(response.data.accessToken);
      const profile = await api.profiles.getProfile();
      if (profile.data) {
        const profileData = profile.data.user;
        await loadSessionData(profileData, profile.data.permission, profile.data.entitlement);
        return profileData;
      }
    }
    throw new Error('Login failed');
  };

  const signup = async (userInfo: { name: string; emailid: string; password: string; profileType: string }) => {

    const response = await api.auth.signUp({
      name: userInfo.name,
      emailid: userInfo.emailid,
      password: userInfo.password,
      profileType: userInfo.profileType
    });


    return response.data;
  };

  const logout = () => {
    api.auth.logout();
    setUser(null);
    setClientProfile(null);
    setSubscription(null);
    setWorkgroups([]);
    setActiveWorkgroupId(null);
    setPermissions([]);
    setEntitlements([]);
  };


  const githubLogin = async () => {
    const authUrl = api.auth.getGitHubAuthUrl();
    window.location.href = authUrl;
  };

  const refreshUser = async () => {
    try {
      const profile = await api.profiles.getProfile();
      if (profile.data) {
        await loadSessionData(profile.data.user, profile.data.permission, profile.data.entitlement);
      }
    } catch (error) {
    }
  };

  const switchWorkgroup = async (workgroupId: number) => {
    const response = await api.auth.switchWorkgroup(workgroupId);
    if (response.data) {
      localStorage.setItem('token', response.data.accessToken);
      const profile = await api.profiles.getProfile();
      if (profile.data) {
        await loadSessionData(profile.data.user, profile.data.permission, profile.data.entitlement);
      }
      setWorkgroupEpoch((epoch) => epoch + 1);
    }
  };

  return (
    <AuthContext.Provider value={{ user, clientProfile, subscription, workgroups, activeWorkgroupId, workgroupEpoch, permissions, entitlements, hasPermission, hasEntitlement, login, signup, githubLogin, logout, refreshUser, switchWorkgroup, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
